import { Component, ElementRef, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';

@Component({
  selector: 'app-streaming',
  templateUrl: './streaming.component.html',
  styleUrls: ['./streaming.component.css']
})
export class StreamingComponent implements OnInit, OnDestroy, AfterViewInit {

  SERVER_URL = 'ws://localhost:8000/ws/asr';
  SAMPLE_RATE = 16000;
  isRecording = false;

  websocket: WebSocket | null = null;
  mediaStream: MediaStream | null = null;
  audioContext: AudioContext | null = null;
  processor: ScriptProcessorNode | null = null;
  currentTranscription = '';

  @ViewChild('statusEl') statusEl!: ElementRef;
  @ViewChild('transcriptionEl') transcriptionEl!: ElementRef;
  @ViewChild('audioLevelBar') audioLevelBar!: ElementRef;
  @ViewChild('hostInput') hostInput!: ElementRef;
  @ViewChild('portInput') portInput!: ElementRef;
  @ViewChild('currentConfigEl') currentConfigEl!: ElementRef;
  @ViewChild('errorEl') errorEl!: ElementRef;
  @ViewChild('infoEl') infoEl!: ElementRef;
  @ViewChild('statsTextEl') statsTextEl!: ElementRef;
  @ViewChild('statsEl') statsEl!: ElementRef;
  @ViewChild('startBtn') startBtn!: ElementRef;
  @ViewChild('stopBtn') stopBtn!: ElementRef;

  ngOnInit(): void {
    // Initialization logic (do not access ViewChilds here)
  }

  ngAfterViewInit(): void {
    this.updateStatus('Ready', 'disconnected');
  }

  ngOnDestroy(): void {
    this.stopRecording();
  }

  updateStatus(status: string, className: string) {
    const el = this.statusEl.nativeElement as HTMLElement;
    el.textContent = status;
    el.className = `status ${className}`;
  }

  showError(message: string) {
    const el = this.errorEl.nativeElement as HTMLElement;
    el.textContent = message;
    el.style.display = 'block';
    setTimeout(() => (el.style.display = 'none'), 5000);
  }

  showInfo(message: string) {
    const el = this.infoEl.nativeElement as HTMLElement;
    el.textContent = message;
    el.style.display = 'block';
    setTimeout(() => (el.style.display = 'none'), 3000);
  }

  updateAudioLevel(level: number) {
    const percentage = Math.min(level * 100, 100);
    this.audioLevelBar.nativeElement.style.width = percentage + '%';
  }

  updateTranscription(text: string, type = 'partial') {
    const el = this.transcriptionEl.nativeElement as HTMLElement;
    if (type === 'partial') {
      this.currentTranscription = text;
      el.innerHTML = `<span class="partial">${text}</span>`;
    } else {
      const span = document.createElement('span');
      span.className = type;
      span.textContent = text + ' ';
      el.appendChild(span);
      el.scrollTop = el.scrollHeight;
      this.currentTranscription = '';
    }
  }

  clearTranscription() {
    const el = this.transcriptionEl.nativeElement as HTMLElement;
    el.innerHTML = 'Ready to transcribe... Speak into your microphone!';
    this.statsEl.nativeElement.style.display = 'none';
  }

  updateServerConfig() {
    const host = this.hostInput.nativeElement.value.trim() || 'localhost';
    const port = this.portInput.nativeElement.value.trim() || '8000';
    this.SERVER_URL = `wss://${host}:${port}/ws/asr`;
    this.currentConfigEl.nativeElement.textContent = this.SERVER_URL;
    this.showInfo(`Server configuration updated: ${this.SERVER_URL}`);
  }

  async connectWebSocket() {
    this.websocket = new WebSocket(this.SERVER_URL);

    this.websocket.onopen = () => {
      this.updateStatus('Connected', 'connected');
      this.showInfo('Connected to ASR server');
    };

    this.websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'partial') {
          this.updateTranscription(data.text, 'partial');
        } else if (data.type === 'final') {
          this.updateTranscription(data.text, 'final');
          if (data.stats) {
            this.statsTextEl.nativeElement.textContent = `Bytes: ${data.stats.bytes_received}, Chunks: ${data.stats.chunks_processed}, Steps: ${data.stats.steps}`;
            this.statsEl.nativeElement.style.display = 'block';
          }
        } else if (data.type === 'error') {
          this.showError(`Server error: ${data.message}`);
        }
      } catch (e: any) {
        this.showError(`Error parsing message: ${e.message}`);
      }
    };

    this.websocket.onclose = () => {
      this.updateStatus('Disconnected', 'disconnected');
      this.showError('Connection to server closed');
    };

    this.websocket.onerror = (error: any) => {
      this.updateStatus('Error', 'disconnected');
      this.showError(`WebSocket error`);
    };
  }

  async startRecording() {
    if (this.isRecording) return;

    try {
      await this.connectWebSocket();

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      this.audioContext = new AudioContext({ sampleRate: this.SAMPLE_RATE });
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.processor.onaudioprocess = (e) => {
        if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);

        let sum = 0;
        for (let i = 0; i < inputData.length; i++) {
          sum += inputData[i] * inputData[i];
        }
        const rms = Math.sqrt(sum / inputData.length);
        this.updateAudioLevel(rms);

        const int16Array = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          int16Array[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
        }

        this.websocket!.send(int16Array.buffer);
      };

      source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      this.isRecording = true;
      this.updateStatus('Recording...', 'connected');
      this.showInfo('Recording started');
      this.startBtn.nativeElement.disabled = true;
      this.stopBtn.nativeElement.disabled = false;
    } catch (error: any) {
      this.showError(`Failed to start recording: ${error.message}`);
    }
  }

  stopRecording() {
    if (!this.isRecording) return;

    this.isRecording = false;

    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send('END');
    }

    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.startBtn.nativeElement.disabled = false;
    this.stopBtn.nativeElement.disabled = true;

    this.updateStatus('Stopped', 'disconnected');
    this.showInfo('Recording stopped');
    this.updateAudioLevel(0);
  }

}
